import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import {
  Button,
  Input,
  List,
  Pagination,
  Popconfirm,
  Tooltip,
  Typography,
} from 'antd'
import { useMemo, useState } from 'react'
import { useDebouncedState } from '../../../../hooks/useDebouncedState'
import VSpace from '../../../common/VSpace'
import {
  downloadRubricMarkdown,
  Rubric,
  RUBRICS_PAGE_SIZE,
} from './rubricsAdminModel'
import {
  useDeleteRubricMutation,
  useRubricsListQuery,
} from './rubricsAdminQueries'
import RubricEditorModal from './RubricEditorModal'

const RubricsExplorer = () => {
  const [page, setPage] = useState(1)
  // Debounce the search so we hit the backend once the user pauses typing; a
  // new term resets to the first page.
  const {
    state: search,
    debouncedState: debouncedSearch,
    setState: setSearch,
  } = useDebouncedState('', 300, () => setPage(1))
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Rubric | null>(null)

  const filters = useMemo(
    () => ({ q: debouncedSearch || undefined, page, pageSize: RUBRICS_PAGE_SIZE }),
    [debouncedSearch, page],
  )
  const { data, isLoading } = useRubricsListQuery(filters)
  const deleteMutation = useDeleteRubricMutation()

  const rubrics = data?.rubrics ?? []
  const total = data?.total ?? 0

  const renderItem = (rubric: Rubric) => (
    <List.Item
      actions={[
        <Tooltip key="delete" title="Delete rubric">
          <Popconfirm
            title="Delete this rubric?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteMutation.mutate(rubric.rubric_id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Tooltip>,
        <Tooltip key="edit" title="Edit rubric">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => setEditing(rubric)}
          />
        </Tooltip>,
        <Tooltip key="download" title="Download markdown">
          <Button
            type="text"
            icon={<DownloadOutlined />}
            onClick={() => downloadRubricMarkdown(rubric)}
          />
        </Tooltip>,
      ]}
    >
      <Typography.Text strong>{rubric.name}</Typography.Text>
    </List.Item>
  )

  return (
    <VSpace>
      <Typography.Title level={4} style={{ marginBottom: 0 }}>
        Rubrics
      </Typography.Title>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Input.Search
          placeholder="Search by name"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 480 }}
        />
        <Tooltip title="New rubric">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreating(true)}
          />
        </Tooltip>
      </div>
      <List
        bordered
        loading={isLoading}
        dataSource={rubrics}
        locale={{ emptyText: 'No rubrics' }}
        renderItem={renderItem}
      />
      {total > 0 && (
        <Pagination
          current={page}
          pageSize={RUBRICS_PAGE_SIZE}
          total={total}
          onChange={setPage}
          showSizeChanger={false}
          hideOnSinglePage
          showTotal={(t) => `${t} rubric(s)`}
          style={{ textAlign: 'right' }}
        />
      )}
      {creating && (
        <RubricEditorModal open onClose={() => setCreating(false)} />
      )}
      {editing && (
        <RubricEditorModal
          open
          rubric={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </VSpace>
  )
}

export default RubricsExplorer
